import { SelectionTemplates } from './SelectionTemplates';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listMoleculeDrawings, type MoleculeDrawingSummary } from '@peruselab/api-client/moleculeDrawings';
import { Icon } from '../../components/Icon';
import { DrawingThumbnail } from '../../components/DrawingThumbnail';
import { MoleculeThumbnail } from '../../components/MoleculeThumbnail';
import { useFavouriteTemplates, type FavouriteRef } from '../../hooks/useFavouriteTemplates';

interface TemplatesRailProps {
  readonly doc: UseDrawingDocumentResult;
  readonly onLoad: (id: string) => void;
  readonly onLoadMolecule: (id: string) => void;
  readonly loadDisabled: boolean;
  // #415 -- Templates is a disclosure in ToolRail's single-open accordion; closed at launch.
  readonly open: boolean;
  readonly onToggle: () => void;
}

const PAGE_SIZE = 25;

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly drawings: readonly MoleculeDrawingSummary[] }
  | { readonly status: 'error'; readonly message: string };

// ADR 0007 P9 + #269 + #415 — the left rail's Templates disclosure: a small curated shelf of the
// user's favourited structures, shown as thumbnails, newest first. A `molecule` favourite (the
// heart on a search-result thumbnail) depicts its structure and, on click, lays a fresh 2D
// depiction onto the canvas; a legacy `drawing` favourite (the File drawer's star pin) shows the
// drawing thumbnail and opens the saved drawing. Each tile carries an X to drop it. Favourites
// follow the signed-in account, with a browser cache for offline use.
export function TemplatesRail({ doc, onLoad, onLoadMolecule, loadDisabled, open, onToggle }: TemplatesRailProps) {
  const { t } = useTranslation();
  const { favourites, toggle, syncStatus, retrySync } = useFavouriteTemplates();
  const [state, setState] = useState<State>({ status: 'loading' });

  const hasDrawingFavourite = favourites.some((ref) => ref.kind === 'drawing');

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const page = await listMoleculeDrawings(0, PAGE_SIZE, '', 'updatedAt', 'desc', 'active');
      setState({ status: 'ready', drawings: page.content });
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : t('workspace.templates.error')
      });
    }
  }, [t]);

  useEffect(() => {
    // Only the drawing-kind tiles need titles from the list; skip the call when there are none.
    if (hasDrawingFavourite) void load();
    else setState({ status: 'ready', drawings: [] });
  }, [hasDrawingFavourite, load]);

  const titleById = new Map(state.status === 'ready' ? state.drawings.map((d) => [d.id, d.title] as const) : []);
  const nameOf = (ref: FavouriteRef) =>
    ref.kind === 'drawing'
      ? titleById.get(ref.id)?.trim() || t('workspace.templates.untitled')
      : t('workspace.templates.savedMolecule');

  // Newest favourite first -- it was just added, so surface it at the top.
  const tiles = [...favourites].reverse();

  return (
    <details id="rail-group-templates" className="workspace-templates" data-testid="templates-rail" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.templates.heading')}
      </summary>

      <SelectionTemplates doc={doc} />

      {syncStatus === 'unsaved' ? (
        <p className="workspace-placeholder" role="status">
          {t('workspace.templates.unsynced')}{' '}
          <button
            type="button"
            onClick={() => void retrySync()}
            className="icon-button"
            aria-label={t('workspace.templates.retry')}
            title={t('workspace.templates.retry')}
          >
            <Icon name="refresh" />
          </button>
        </p>
      ) : null}

      {state.status === 'loading' ? <p className="workspace-placeholder">{t('common.loading')}</p> : null}

      {state.status === 'error' ? (
        <p className="import-error" role="alert">
          {state.message}{' '}
          <button
            type="button"
            onClick={() => void load()}
            className="icon-button"
            aria-label={t('workspace.templates.retry')}
            title={t('workspace.templates.retry')}
          >
            <Icon name="refresh" />
          </button>
        </p>
      ) : null}

      {state.status !== 'loading' && tiles.length === 0 ? (
        <p className="workspace-placeholder">{t('workspace.templates.empty')}</p>
      ) : null}

      {tiles.length > 0 ? (
        <ul className="workspace-templates-grid">
          {tiles.map((ref) => (
            <li key={`${ref.kind}:${ref.id}`} className="workspace-templates-item">
              <button
                type="button"
                className="workspace-templates-load"
                onClick={() => (ref.kind === 'drawing' ? onLoad(ref.id) : onLoadMolecule(ref.id))}
                disabled={loadDisabled}
                aria-label={t('workspace.templates.loadNamed', { name: nameOf(ref) })}
                title={nameOf(ref)}
              >
                {ref.kind === 'drawing' ? (
                  <DrawingThumbnail drawingId={ref.id} />
                ) : (
                  <MoleculeThumbnail moleculeId={ref.id} size={96} />
                )}
              </button>
              <button
                type="button"
                className="workspace-templates-remove"
                aria-label={t('workspace.templates.remove', { name: nameOf(ref) })}
                title={t('workspace.templates.remove', { name: nameOf(ref) })}
                onClick={() => toggle(ref)}
              >
                <Icon name="close" size="1rem" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </details>
  );
}
